// Status placeholder for the contrast / context matrix panels.
//
// Two visually distinct states share the same vertical slot so the panel
// chrome (toolbar, border) stays steady while the body swaps:
//   - "loading"  →  three pulsing dots + status text. The animation
//                   tells the eye that something is in flight; the text
//                   names what it's waiting for.
//   - "empty"    →  text only, no motion. Communicates "we got an answer
//                   and it was nothing" — distinct from "we don't have
//                   an answer yet."
//
// Both states center themselves in the available space and use the same
// muted text color so neither shouts. The motion is the only difference,
// which is also the only signal that matters: motion = waiting, still =
// done. Refusing to share a render path with the DotGrid means the
// placeholder grid never gets confused with real-but-sparse data.
export default function GridStatus({ state, label }) {
  const isLoading = state === "loading";
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-[10px] text-[11px] leading-[1.3] text-[color:var(--muted-soft)]">
      {isLoading ? (
        <PulsingDots />
      ) : (
        <span
          aria-hidden="true"
          className="inline-block h-[1px] w-[18px] bg-[color:var(--line-strong)]"
        />
      )}
      <span className={isLoading ? "text-[color:var(--muted)]" : "text-[color:var(--muted-soft)]"}>
        {label}
      </span>
    </div>
  );
}

function PulsingDots() {
  return (
    <span className="inline-flex items-center gap-[4px]" aria-hidden="true">
      <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:0ms]" />
      <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:160ms]" />
      <span className="inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--muted)] animate-pulse [animation-delay:320ms]" />
    </span>
  );
}

// Overlay shown when a refetch is in flight but stale data is still on
// screen (useQuery's stale-while-revalidate semantics across param
// changes — e.g. hovering from swimlane cell A to cell B before B's
// matrix lands). Sits absolutely positioned over the content area; the
// parent must be `position: relative`. Stale content stays visible
// underneath at reduced contrast so the panel doesn't blink, but the
// dim + pulse makes it obvious that what's on screen no longer
// describes the user's current pointer target.
export function RevalidatingOverlay({ active, label = "Refreshing…" }) {
  if (!active) return null;
  return (
    <div
      aria-hidden="true"
      // pointer-events: none lets hover/click pass through to the grid
      // beneath — useful when the refresh is fast enough that the user
      // is still aiming at the same cell. color-mix lets us dim the
      // underlying grid against the panel's own --bg in either theme
      // without hard-coding the hex; Tailwind's `/<alpha>` syntax on
      // var() colors isn't reliable in v4 when the var resolves to hex.
      style={{ background: "color-mix(in oklab, var(--bg) 60%, transparent)" }}
      className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-[10px] text-[11px] leading-[1.3]"
    >
      <PulsingDots />
      <span className="text-[color:var(--muted)]">{label}</span>
    </div>
  );
}
